import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Trophy, Lock, Filter, Star, Flame, Target, Map, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ProgressBar, XpBar } from '@/components/ui/ProgressBar';
import { achievements, journeys, species } from '@/data';
import { useProgress } from '@/hooks/useProgress';
import type { AchievementRarity } from '@/types';

type FilterOption = 'all' | 'unlocked' | 'locked' | AchievementRarity;

const rarityOrder: AchievementRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

const rarityColors: Record<AchievementRarity, string> = {
  common: 'from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600',
  uncommon: 'from-green-200 to-green-300 dark:from-green-800 dark:to-green-700',
  rare: 'from-blue-200 to-blue-300 dark:from-blue-800 dark:to-blue-700',
  epic: 'from-purple-200 to-purple-300 dark:from-purple-800 dark:to-purple-700',
  legendary: 'from-amber-200 to-yellow-300 dark:from-amber-700 dark:to-yellow-600',
};

const rarityIcons: Record<AchievementRarity, string> = {
  common: '⭐',
  uncommon: '🌟',
  rare: '💎',
  epic: '👑',
  legendary: '🏆',
};

export function Achievements() {
  const [filter, setFilter] = useState<FilterOption>('all');
  const { progress } = useProgress();

  const unlockedIds = Object.keys(progress.achievements.unlocked);
  const totalUnlocked = unlockedIds.length;

  // Calculate level from journey
  const globalJourney = journeys.find((j) => j.id === 'global-expert');
  const currentLevel =
    globalJourney?.levels.findLast((l) => l.xpRequired <= progress.globalStats.totalXp) ||
    globalJourney?.levels[0];
  const nextLevel = globalJourney?.levels.find(
    (l) => l.xpRequired > progress.globalStats.totalXp
  );

  const filteredAchievements = useMemo(() => {
    let filtered = [...achievements];

    if (filter === 'unlocked') {
      filtered = filtered.filter((a) => unlockedIds.includes(a.id));
    } else if (filter === 'locked') {
      filtered = filtered.filter((a) => !unlockedIds.includes(a.id));
    } else if (rarityOrder.includes(filter as AchievementRarity)) {
      filtered = filtered.filter((a) => a.rarity === filter);
    }

    // Sort: unlocked first, then by rarity
    return filtered.sort((a, b) => {
      const aUnlocked = unlockedIds.includes(a.id);
      const bUnlocked = unlockedIds.includes(b.id);

      if (aUnlocked && !bUnlocked) return -1;
      if (!aUnlocked && bUnlocked) return 1;

      return rarityOrder.indexOf(b.rarity) - rarityOrder.indexOf(a.rarity);
    });
  }, [filter, unlockedIds]);

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header Stats */}
      <Card className="mb-6 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 border-amber-200 dark:border-amber-700">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-slate-800 dark:text-white">
              Trophy Case
            </h1>
            <p className="text-slate-600 dark:text-slate-400">
              {totalUnlocked}/{achievements.length} achievements unlocked
            </p>
          </div>
        </div>

        {/* Level Progress */}
        {globalJourney && currentLevel && (
          <XpBar
            currentXp={progress.globalStats.totalXp}
            levelXp={currentLevel.xpRequired}
            nextLevelXp={nextLevel?.xpRequired || currentLevel.xpRequired}
            level={currentLevel.level}
            levelName={currentLevel.name}
          />
        )}
      </Card>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="text-center py-3">
          <Flame className="w-6 h-6 mx-auto mb-1 text-orange-500" />
          <p className="text-xl font-bold text-slate-800 dark:text-white">
            {progress.streaks.current}
          </p>
          <p className="text-xs text-slate-500">Day Streak</p>
        </Card>
        <Card className="text-center py-3">
          <Target className="w-6 h-6 mx-auto mb-1 text-green-500" />
          <p className="text-xl font-bold text-slate-800 dark:text-white">
            {progress.globalStats.totalCorrect}
          </p>
          <p className="text-xs text-slate-500">Correct</p>
        </Card>
        <Card className="text-center py-3">
          <Map className="w-6 h-6 mx-auto mb-1 text-blue-500" />
          <p className="text-xl font-bold text-slate-800 dark:text-white">
            {progress.speciesViewed.length}/{species.length}
          </p>
          <p className="text-xs text-slate-500">Species</p>
        </Card>
      </div>

      {/* Journey Links */}
      <div className="mb-6">
        <h2 className="text-lg font-display font-bold text-slate-800 dark:text-white mb-3">
          Your Journeys
        </h2>
        <div className="space-y-2">
          {journeys.slice(0, 4).map((journey) => {
            const journeyProgress = progress.journeys[journey.id];
            const currentJourneyLevel =
              journey.levels.findLast(
                (l) => l.xpRequired <= (journeyProgress?.currentXp || 0)
              ) || journey.levels[0];

            return (
              <Link key={journey.id} to={`/journey/${journey.id}`}>
                <Card hoverable className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${journey.theme.color}20` }}
                  >
                    <span className="text-xl">
                      {journey.type === 'species'
                        ? '🦦'
                        : journey.type === 'challenge'
                        ? '⚡'
                        : '🗺️'}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-800 dark:text-white text-sm">
                      {journey.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      Level {currentJourneyLevel.level} · {currentJourneyLevel.name}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </Card>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide -mx-4 px-4">
        {[
          { value: 'all', label: 'All' },
          { value: 'unlocked', label: '✓ Unlocked' },
          { value: 'locked', label: '🔒 Locked' },
          { value: 'legendary', label: '🏆 Legendary' },
          { value: 'epic', label: '👑 Epic' },
          { value: 'rare', label: '💎 Rare' },
        ].map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value as FilterOption)}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filter === option.value
                ? 'bg-amber-500 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Achievements Grid */}
      <motion.div
        className="grid grid-cols-2 gap-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        {filteredAchievements.map((achievement) => {
          const isUnlocked = unlockedIds.includes(achievement.id);
          const unlockData = progress.achievements.unlocked[achievement.id];
          const progressValue = progress.achievements.progress[achievement.id] || 0;

          return (
            <motion.div
              key={achievement.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.02 }}
            >
              <Card
                className={`h-full ${
                  isUnlocked
                    ? `bg-gradient-to-br ${rarityColors[achievement.rarity]}`
                    : 'bg-slate-100 dark:bg-slate-800'
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  {/* Icon */}
                  <div
                    className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl mb-2 ${
                      isUnlocked
                        ? 'bg-white/50 dark:bg-black/20'
                        : 'bg-slate-200 dark:bg-slate-700'
                    }`}
                  >
                    {isUnlocked ? rarityIcons[achievement.rarity] : <Lock className="w-6 h-6 text-slate-400" />}
                  </div>

                  {/* Name */}
                  <h3
                    className={`font-semibold text-sm mb-1 ${
                      isUnlocked
                        ? 'text-slate-800 dark:text-white'
                        : 'text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    {achievement.name}
                  </h3>

                  {/* Description */}
                  <p
                    className={`text-xs mb-2 ${
                      isUnlocked
                        ? 'text-slate-600 dark:text-slate-300'
                        : 'text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    {achievement.description}
                  </p>

                  {/* Rarity Badge */}
                  <Badge
                    variant="achievement"
                    achievementRarity={achievement.rarity}
                    className="mb-2"
                  >
                    {achievement.rarity}
                  </Badge>

                  {/* Progress or XP */}
                  {isUnlocked ? (
                    <p className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                      +{achievement.xpReward} XP
                    </p>
                  ) : (
                    progressValue > 0 && (
                      <div className="w-full">
                        <ProgressBar
                          value={progressValue}
                          size="sm"
                          color="primary"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          {Math.round(progressValue)}%
                        </p>
                      </div>
                    )
                  )}
                </div>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      {filteredAchievements.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-500">No achievements found.</p>
        </div>
      )}
    </div>
  );
}
